/**
 * LLM utilities for dataset generation functionality.
 *
 * This module provides prompts and schemas for generating dataset rows
 * using AI models through the magician system.
 */

import {z} from 'zod';

/**
 * Zod schema for a single dataset row.
 * Flexible schema that allows any string keys with various value types.
 */
export const DatasetRowSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.undefined()])
);

/**
 * Type for a dataset row based on the schema.
 */
export type DatasetRow = z.infer<typeof DatasetRowSchema>;

/**
 * System prompt for generating dataset rows.
 * This prompt works in conjunction with prepareSingleShotMessages which provides
 * the specific context (existing rows, number of rows to generate) separately.
 */
export const createDatasetRowsPrompt = `You are an AI assistant that generates dataset rows based on user descriptions.

Your task is to generate new rows of data that match any existing dataset structure and style provided in the context.

Guidelines for datasets with existing data:
- Generate new rows with the EXACT same column structure as the existing data
- Match the data types and patterns shown in the sample rows
- Create diverse, realistic data that fits the same theme/domain
- Ensure the new data complements but doesn't duplicate the existing rows
- Maintain consistency with the existing data style and format

Guidelines for new datasets:
- Generate diverse, realistic data that would be useful for the described use case
- Ensure data types are appropriate (strings, numbers, booleans, etc.)
- Make the data varied and interesting, not repetitive
- Follow common dataset patterns and best practices
- Column names should be descriptive and follow snake_case convention
- Values should be realistic and make sense in context

Return your response as a JSON array of objects, where each object represents one row of data.
Each row should have the same column structure as any existing data provided in the context.

Example output format:
[
  {"column1": "value1", "column2": 123, "column3": true},
  {"column1": "value2", "column2": 456, "column3": false}
]`;
